import { useEffect, useState } from 'react';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { isCapacitorNative } from '@utils/capacitor-utils';
import { PluginListenerHandle } from '@capacitor/core';

interface DeepLinkParams {
  url: string | null;
  pathname: string | null;
  params: URLSearchParams | null;
}

export const useDeepLink = () => {
  const [deepLinkData, setDeepLinkData] = useState<DeepLinkParams>({
    url: null,
    pathname: null,
    params: null
  });

  useEffect(() => {
    if (!isCapacitorNative()) return;

    let listenerHandle: PluginListenerHandle | null = null;

    const setupListener = async () => {
      // Listen for app URL events (when app is opened via deep link)
      listenerHandle = await App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
        console.log('>>> Deep link received:', event.url);

        try {
          let pathname: string;
          let params: URLSearchParams;

          // TODO: DRY
          // Check if it's a universal link (https://) or custom scheme (myefka://)
          if (event.url.startsWith('https://') || event.url.startsWith('http://')) {
            // Universal link - parse as standard URL
            const urlObj = new URL(event.url);
            pathname = urlObj.pathname;
            params = new URLSearchParams(urlObj.search);
            console.info(' >>> Universal link detected', params);
          } else {
            // Custom scheme URL - parse manually
            const urlParts = event.url.replace('myefka://', '').split('?');
            pathname = '/' + urlParts[0]; // Add leading slash for routing
            const queryString = urlParts[1] || '';
            params = new URLSearchParams(queryString);
            console.info(' >>> Custom scheme link detected', params);
          }

          setDeepLinkData({ url: event.url, pathname, params });
        } catch (error) {
          console.error('Error parsing deep link URL:', error);
        }
      });

      // Check if app was opened with a URL initially
      const appUrl = await App.getLaunchUrl();
      if (appUrl?.url) {
        console.log('🔗 App launched with URL:', appUrl.url);

        try {
          let pathname: string;
          let params: URLSearchParams;

          // TODO: DRY
          // Check if it's a universal link (https://) or custom scheme (myefka://)
          if (appUrl.url.startsWith('https://') || appUrl.url.startsWith('http://')) {
            // Universal link - parse as standard URL
            const urlObj = new URL(appUrl.url);
            pathname = urlObj.pathname;
            params = new URLSearchParams(urlObj.search);
            console.log('>>> Universal link detected on launch');
          } else {
            // Custom scheme URL - parse manually
            const urlParts = appUrl.url.replace('myefka://', '').split('?');
            pathname = '/' + urlParts[0]; // Add leading slash for routing
            const queryString = urlParts[1] || '';
            params = new URLSearchParams(queryString);
            console.log('>>> Custom scheme link detected on launch');
          }

          setDeepLinkData({
            url: appUrl.url,
            pathname,
            params
          });
        } catch (error) {
          console.error('Error parsing launch URL:', error);
        }
      }
    };

    setupListener();

    return () => {
      if (listenerHandle) {
        listenerHandle.remove();
      }
    };
  }, []);

  // Helper function to clear deep link data after processing
  const clearDeepLink = () => {
    setDeepLinkData({
      url: null,
      pathname: null,
      params: null
    });
  };

  return {
    deepLinkData,
    clearDeepLink,
    hasDeepLink: !!deepLinkData.url
  };
};
